import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { CopyIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { useCallbackRef } from '../../../core/hooks/useCallbackRef.js';
import { useCopyToClipboard } from '../../../core/utils/useCopyToClipboard.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import { codeSnippetActionsMenuMessages as messages } from './actions-menu-messages.js';

/**
 * Props for the `CodeSnippet.Copy` slot.
 * @public
 */
export interface CodeSnippetCopyProps {
  /**
   * Callback fired when the copy action is triggered.
   */
  onCopy?: () => void;
}

/**
 * Slot that enables a Copy action inside `CodeSnippet.ActionsMenu`.
 * @public
 */
export const Copy = (props: CodeSnippetCopyProps): null => null;
(Copy as typeof Copy & { displayName: string }).displayName =
  'CodeSnippet.Copy';

/**
 * Props for the `CopyInternal` component.
 */
interface CopyInternalProps {
  /**
   * Layout size of the snippet, matched by the button.
   */
  size: 'default' | 'condensed';
  /**
   * The snippet's plain-text content, written to the clipboard.
   */
  copyText: string;
  /**
   * Callback fired once the content was successfully copied.
   */
  onCopy?: () => void;
}

/**
 * Renders `CodeSnippet.Copy` as a standalone button, with a tooltip that confirms
 * a successful copy.
 * @internal
 */
export const CopyInternal = ({ size, copyText, onCopy }: CopyInternalProps) => {
  const intl = useIntl();
  const [copyToClipboard, copied, setCopied] = useCopyToClipboard(copyText);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const onCopyProp = useCallbackRef(onCopy);

  const copyLabel = intl.formatMessage(messages.copy);

  useEffect(() => {
    if (copied) {
      onCopyProp();
    }
  }, [copied, onCopyProp]);

  return (
    <Tooltip
      text={copied ? intl.formatMessage(messages.copied) : copyLabel}
      placement="top"
      open={isTooltipOpen || copied}
      delay="none"
      onOpenChange={(isOpen) => {
        setIsTooltipOpen(isOpen);
        if (!isOpen) {
          setCopied(false);
        }
      }}
    >
      <Button
        variant="default"
        size={size}
        aria-label={copyLabel}
        onClick={() => {
          // Reset first so a successful copy always produces a `false -> true`
          // transition, even when `copied` is still latched `true` from a previous copy.
          setCopied(false);
          copyToClipboard();
        }}
        data-dt-children-name="Copy"
      >
        <Button.Prefix>
          <CopyIcon />
        </Button.Prefix>
      </Button>
    </Tooltip>
  );
};
